"use client";

import Link from "next/link";
import Navbar from "@/app/components/Navbar";
import Footer from "@/app/components/Footer";

const SECTIONS = [
  {
    id: "connect",
    title: "Connect your Instagram account",
    body: "AureliusCo connects to Instagram through Meta's official Graph API using OAuth 2.0 — you authorize access from your dashboard, and AureliusCo never sees or stores your Instagram password. Only a revocable access token is stored, which you can disconnect at any time from Accounts in your dashboard.",
  },
  {
    id: "create-automation",
    title: "Create an automation",
    body: "An automation is a named rule that watches for specific triggers on your connected account. When you create one, you choose whether it fires on Instagram Comments, Direct Messages, or both, and you can optionally scope it to specific posts instead of your whole account.",
  },
  {
    id: "keywords",
    title: "How keyword triggers work",
    body: "Each automation has a list of keywords (e.g. \"pricing, demo, interested\"). When an incoming DM or comment contains one of those keywords, the automation fires instantly — matching happens in real time, not on a delay or batch schedule.",
  },
  {
    id: "reply-types",
    title: "Fixed replies vs. Smart AI replies",
    body: "You choose how each automation responds: a Fixed Message sends the exact text (plus an optional button with a label and URL, or an image with a caption) every time. A Smart AI reply instead uses a prompt you write, and generates a context-aware response based on the incoming message — useful when you want replies to feel personal rather than templated.",
  },
  {
    id: "comment-vs-dm",
    title: "Comment automation vs. DM automation",
    body: "Comment triggers watch for a keyword in a comment on your posts and can optionally auto-like the comment before replying. DM triggers watch your direct messages. You can run both trigger types from the same automation, or split them into separate automations with different replies.",
  },
  {
    id: "accounts",
    title: "Managing multiple Instagram accounts",
    body: "Each plan allows a set number of connected accounts — Basic (1), Growth (3), Elite (10). You manage all connected accounts from one dashboard login, switching between them without re-authenticating each time.",
  },
  {
    id: "rate-limits",
    title: "Rate limits and account protection",
    body: "Every plan has a daily reply cap (Basic: 10/day, Growth: 500/day, Elite: 2,000/day) to keep engagement patterns within Instagram's guidelines and protect your account from being flagged. Once the daily cap is reached, automations pause until it resets.",
  },
  {
    id: "analytics",
    title: "Logs and analytics",
    body: "Every automated reply is logged with its status (replied, pending, hard-stopped, or no rule matched). Your dashboard shows message volume, reply counts per automation, and lets you review exactly what was sent and when.",
  },
  {
    id: "control",
    title: "Pausing, editing, or overriding automations",
    body: "Any automation can be paused, edited, or deleted from your dashboard at any time — changes apply immediately to new incoming messages. Nothing runs that you haven't explicitly configured.",
  },
  {
    id: "billing",
    title: "Trial, billing, and plan changes",
    body: "Every plan includes a 14-day free trial. Billing is monthly or annual (annual saves 20%). You can upgrade at any time — remaining days on your current plan are credited toward the new one. Downgrading requires cancelling your current plan first. See full plan details on the Pricing page.",
  },
];

const FAQS = [
  {
    q: "How do I connect my Instagram account to AureliusCo?",
    a: "Go to Accounts in your dashboard and click Connect. You'll be redirected to Instagram's official OAuth login — approve access there, and your account appears in the dashboard immediately. No password is ever entered into AureliusCo.",
  },
  {
    q: "Can I have different automations for different posts?",
    a: "Yes. When creating an automation, you can scope its triggers to specific posts instead of applying it account-wide, so different posts can have different keyword replies.",
  },
  {
    q: "What happens when I hit my daily reply limit?",
    a: "Automations pause until the daily cap resets. Upgrading to a higher plan raises the limit (Basic: 10/day, Growth: 500/day, Elite: 2,000/day).",
  },
  {
    q: "Does AureliusCo store my Instagram password?",
    a: "No. AureliusCo uses OAuth 2.0 through Instagram's official Graph API and stores only a revocable access token, never a password.",
  },
];

export default function DocsClient() {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://www.aureliusco.in" },
      { "@type": "ListItem", position: 2, name: "Docs", item: "https://www.aureliusco.in/docs" },
    ],
  };

  return (
    <>
      <Navbar />
      <div style={{ background: "#0A0908", minHeight: "100vh", color: "#F5F0E8" }}>
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

        <main className="max-w-3xl mx-auto px-4 sm:px-8" aria-label="Documentation" style={{ paddingTop: "clamp(5rem, 10vw, 8rem)", paddingBottom: "clamp(4rem, 8vw, 6rem)" }}>
          <div className="mb-14">
            <p className="text-xs uppercase tracking-[0.25em] mb-4 flex items-center gap-2" style={{ color: "#C9A84C", fontFamily: "'Montserrat', sans-serif" }}>
              <span className="h-px w-5 inline-block" style={{ background: "#C9A84C" }} />
              Docs
            </p>
            <h1 style={{ fontFamily: "'Cormorant', Georgia, serif", fontSize: "clamp(2.5rem, 5vw, 4rem)", fontWeight: 400, lineHeight: 1.1, color: "#F5F0E8" }}>
              How AureliusCo works
            </h1>
            <p className="mt-4 text-sm leading-relaxed" style={{ color: "rgba(245,240,232,0.55)", fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
              AureliusCo automates Instagram DM and comment replies using keyword triggers, built entirely on Instagram&apos;s official Graph API. Here&apos;s exactly how the pieces work.
            </p>
          </div>

          <nav aria-label="Table of contents" className="mb-16 p-6" style={{ border: "1px solid rgba(201,168,76,0.09)", background: "rgba(255,255,255,0.018)" }}>
            <p className="text-xs uppercase tracking-[0.15em] mb-4" style={{ color: "rgba(201,168,76,0.6)", fontFamily: "'Montserrat', sans-serif" }}>On this page</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {SECTIONS.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`} className="text-xs" style={{ color: "rgba(245,240,232,0.5)", fontFamily: "'Montserrat', sans-serif" }}>
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className="space-y-12 mb-20">
            {SECTIONS.map((s) => (
              <section key={s.id} id={s.id} aria-labelledby={`${s.id}-heading`}>
                <h2 id={`${s.id}-heading`} className="mb-3" style={{ fontFamily: "'Cormorant', Georgia, serif", fontSize: "1.6rem", fontWeight: 400, color: "#F5F0E8" }}>
                  {s.title}
                </h2>
                <p className="text-sm leading-relaxed" style={{ color: "rgba(245,240,232,0.55)", fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
                  {s.body}
                </p>
              </section>
            ))}
          </div>

          <div className="max-w-3xl mx-auto">
            <h2 className="mb-10" style={{ fontFamily: "'Cormorant', Georgia, serif", fontSize: "clamp(1.75rem, 3vw, 2.5rem)", fontWeight: 400, color: "#F5F0E8" }}>
              Common questions
            </h2>
            <div className="space-y-6">
              {FAQS.map((faq) => (
                <div key={faq.q} className="p-6" style={{ border: "1px solid rgba(201,168,76,0.09)", background: "rgba(255,255,255,0.018)" }}>
                  <h3 className="text-sm font-medium mb-2" style={{ color: "#C9A84C", fontFamily: "'Montserrat', sans-serif" }}>{faq.q}</h3>
                  <p className="text-xs leading-relaxed" style={{ color: "rgba(245,240,232,0.55)", fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>{faq.a}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="max-w-3xl mx-auto mt-16 text-center text-xs" style={{ color: "rgba(245,240,232,0.35)", fontFamily: "'Montserrat', sans-serif" }}>
            Ready to get started?{" "}
            <Link href="/pricing" className="underline" style={{ color: "rgba(201,168,76,0.8)" }}>View pricing</Link>
            {" "}or{" "}
            <Link href="/contact" className="underline" style={{ color: "rgba(201,168,76,0.8)" }}>talk to the team</Link>.
          </div>
        </main>
      </div>
      <Footer />
    </>
  );
}
